import React from 'react';
import { Book, Star, ShoppingCart, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BookPage = () => {
  const books = [
    {
      id: 1,
      title: "M.E. T.I.M.E.: How Small Moments Create Big Purpose",
      author: "TyMagic Robinson",
      coverImage: "https://m.media-amazon.com/images/I/31PjtkjMfLL._SY445_SX342_.jpg",
      rating: 5.0,
      reviewCount: 1,
      publicationDate: "September 22, 2025",
      pages: 131,
      readingAge: "12-18 years",
      isbn: "979-8266573642",
      amazonUrl: "https://www.amazon.com/dp/B0FSLJRG6V",
      formats: [
        { type: "Hardcover", price: "$27.99" },
        { type: "Paperback", price: "$16.99" },
        { type: "Kindle", price: "$9.99" }
      ],
      description: "Life moves fast. It's easy to feel stuck, distracted, or unsure about who you are and where you're going. M.E. T.I.M.E. is your guide to slowing down, taking a deep breath, and living with purpose every single day. Through simple, powerful models like DAILY, HOLY, CARRY, EASY, and READY, TyMagic Robinson gives you practical tools to grow in confidence, build stronger relationships, and turn everyday moments into meaningful ones."
    },
    {
      id: 2,
      title: "I Can't Make This Up: The How To, Unlock You",
      author: "Curtis Martin & TyMagic Robinson",
      coverImage: "https://m.media-amazon.com/images/I/815O6gkKkIL._SY342_.jpg",
      rating: 0,
      reviewCount: 0,
      publicationDate: "January 4, 2026",
      pages: 75,
      readingAge: "12-18 years",
      isbn: "B0GDZ5P7NS",
      amazonUrl: "https://www.amazon.com/dp/B0GDZ5P7NS",
      formats: [
        { type: "Paperback", price: "$16.99" },
        { type: "Kindle", price: "$9.99" }
      ],
      description: "This book is not about motivation. It's about responsibility. Life doesn't change because we wish harder. It changes when we choose honesty, discipline, and action—especially when no one is watching. Through personal stories and practical insights, this book challenges you to look at your life with eyes wide open—to stop blaming circumstances and start leading yourself."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/10 rounded-full border border-[#E6B933]/20">
              <p className="text-[#E6B933] font-semibold text-sm">Books by TyMagic Robinson</p>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Grow With <span className="text-[#E6B933]">ME</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover powerful tools for personal growth, leadership, and living with purpose through TyMagic Robinson's inspiring books.
            </p>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {books.map((book) => (
              <Card key={book.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 group">
                <div className="grid md:grid-cols-2 gap-6 p-6">
                  {/* Book Cover */}
                  <div className="flex justify-center">
                    <div className="relative">
                      <div className="absolute -inset-4 bg-[#E6B933]/20 rounded-2xl blur-xl group-hover:bg-[#E6B933]/30 transition-all duration-300"></div>
                      <img 
                        src={book.coverImage} 
                        alt={book.title} 
                        className="relative rounded-lg shadow-2xl w-full max-w-[200px] hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>

                  {/* Book Info */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
                        {book.title}
                      </h2>
                      <p className="text-gray-600 mb-3">by {book.author}</p>
                      
                      {book.rating > 0 && (
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className="text-[#E6B933] fill-[#E6B933]" size={14} />
                            ))}
                          </div>
                          <span className="text-gray-500 text-sm">{book.rating} ({book.reviewCount} review)</span>
                        </div>
                      )}

                      <div className="space-y-1 text-sm text-gray-600 mb-4">
                        <p><span className="font-semibold">Published:</span> {book.publicationDate}</p>
                        <p><span className="font-semibold">Pages:</span> {book.pages}</p>
                        <p><span className="font-semibold">Reading Age:</span> {book.readingAge}</p>
                      </div>

                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                        {book.description}
                      </p>
                    </div>

                    <div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {book.formats.map((format, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {format.type}: {format.price}
                          </span>
                        ))}
                      </div>

                      <a href={book.amazonUrl} target="_blank" rel="noopener noreferrer">
                        <Button className="w-full bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold py-3 transition-all duration-300 hover:scale-105">
                          <ShoppingCart className="mr-2" size={18} />
                          Buy on Amazon
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About the Author */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            About the <span className="text-[#E6B933]">Author</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#E6B933]/20 rounded-full blur-xl"></div>
                <img 
                  src="https://m.media-amazon.com/images/S/amzn-author-media-prod/al285c5fccmvlirb5u85th22g5._SY600_.jpg" 
                  alt="TyMagic Robinson - Author and Coach" 
                  className="relative rounded-full w-80 h-80 object-cover shadow-2xl"
                />
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">TyMagic Robinson</h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                TyMagic Robinson is a coach, author, and mentor dedicated to helping athletes and young people discover their true potential. With over 13 years of experience in sports performance, fitness, and character development, he has built a reputation for transforming talent through discipline, mindset, and purpose-driven training.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                As the founder of <strong>Missing Element Sports Performance</strong>, TyMagic designs programs that blend physical training with leadership and life skills. His writing captures the same passion he brings to the field—equipping readers with tools to build confidence, resilience, and success both in sports and in life.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                When he's not coaching or writing, TyMagic is focused on empowering communities, developing youth programs, and mentoring the next generation of leaders through <strong>The WHY ME Foundation</strong>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://www.amazon.com/TyMagic-Robinson/e/B0FT8W83MF" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold transition-all duration-300">
                    <ExternalLink className="mr-2" size={18} />
                    More from TyMagic Robinson
                  </Button>
                </a>
                <a href="https://missingelementsports.com" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold transition-all duration-300">
                    Visit Missing Element Sports
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">
            Start Your <span className="text-[#E6B933]">Growth Journey</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Discover the tools you need to unlock your potential and live with purpose.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://www.amazon.com/TyMagic-Robinson/e/B0FT8W83MF" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                <ShoppingCart className="mr-2" size={20} />
                Shop All Books
              </Button>
            </a>
          </div>
          <p className="text-sm text-gray-400 mt-4">Available in Hardcover, Paperback, and Kindle formats</p>
        </div>
      </section>
    </div>
  );
};

export default BookPage;
